import { defineComponent } from 'vue';
import { NForm, NInput, NInputNumber, FormRules, NAlert, NFormItemGi, NGrid, NAutoComplete, NDatePicker } from 'naive-ui';
import { mapStores } from 'pinia';

import { Station } from '~/api/models/station.model';
import AvoltaMap from '~/components/leaflet-map/avolta-map.vue';

import ModalBase from '~/components/modals/modal-base';
import Modal from '~/components/modals/modal.vue';
import { useStationStore } from '~/store/station.store';
import { debounce } from 'lodash';
import { EditionForm } from './edition-form';
import { GeocoderAdressComponentTypeEnum, GeocoderResponse, GeocoderResult, GeocoderResultType } from '~/plugins/geocoder/models/geocoder.model';


export default defineComponent({

    extends: ModalBase,

    components: {
        AvoltaMap,
        Modal,
        NForm,
        NFormItemGi,
        NInput,
        NInputNumber,
        NAutoComplete,
        NAlert,
        NGrid,
        NDatePicker,
    },

    data() {
        const result: EditionForm = { 
            city: '', 
            address: null, 
            latitude: null, 
            longitude: null, 
            country: null,
            state: null,
            entryPoint: null,
            postcode: null,
            installed: null,
        };
        return {
            stationToUpdate: null as Station,
            mapStation: null as Station,
            promise: null as ((value: Promise<Partial<Station>>) => void) | null,
            valid: true,
            pristine: true,
            failed: false,


            debounceSearch: debounce((query) => this.search(query), 500),
            autoCompleteOptions: [] as { label: string, value: GeocoderResult }[],
            loading: false,

            center: [0, 0],
            zoom: 1,

            query: '',

            result: result as EditionForm,
            rules: {
                address: { required: false, trigger: 'blur', message: ' '},
                entryPoint: { required: false, trigger: 'blur', message: ' ' },
                postcode: { required: false, trigger: 'blur', message: ' '},
                city: { required: false, trigger: 'blur', message: ' ' },
                country: { required: false, trigger: 'blur', message: ' '},
                state: { required: false, trigger: 'blur', message: ' ' },
                latitude: { required: false, trigger: 'blur', type: 'number', message: ' ' },
                longitude: { required: false, trigger: 'blur', type: 'number', message: ' ' },
                installed: { required: false, trigger: 'blur', type: 'date', messages: ' '},
            } as FormRules,
        }
    },

    computed: {
        ...mapStores(useStationStore),
    },

    methods: {

        async onMapClick({ lat, lng }: { lat: number, lng: number }): Promise<void> {
            this.result.latitude = lat;
            this.result.longitude = lng;
            this.mapStation = { ...this.mapStation, location: { lat, lon: lng } }


            const response = await this.$geocoder.searchByLocation(lat, lng);
            if(!response.results?.length) {
                return;
            }

            const result = response.results.find(r => r.types.includes(GeocoderResultType.STREET_ADDRESS));
            if(!result) {
                return;
            }

            this.result.city = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.LOCALITY))?.long_name;
            this.result.address = [
                result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.STREET_NUMBER))?.long_name,
                result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.ROUTE))?.long_name,
                result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.NEIGHBORHOOD))?.long_name,
            ].filter(str => str).join(', ');
            this.result.postcode = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.POSTAL_CODE))?.long_name;
            this.result.state = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.ADMINISTRATIVE_LEVEL_1))?.long_name;
            this.result.country = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.COUNTRY))?.long_name;
        },

        async editStation(station: Station): Promise<void> {
            this.stationToUpdate = this.mapStation = { ...station };
            this.result = {
                entryPoint: station.metadata?.['elichens/public/entry_point'] as string || '',
                address: station.metadata?.['elichens/public/address'] as string || '',
                city: station.metadata?.['elichens/public/locality'] as string || '',
                postcode: station.metadata?.['elichens/public/postcode'] as string || '',
                state: station.metadata?.['elichens/public/state'] as string || '',
                country: station.metadata?.['elichens/public/country'] as string || '',
                latitude: station.location?.lat || null,
                longitude: station.location?.lon || null,
                installed: station.metadata?.['elichens/public/installed'] || null,
            }

            if(!station.location?.lat || !station.location?.lon) {
                const location: GeolocationPosition = await this.$geolocalization.getCurrentLocation();
                this.center = [location.coords.latitude, location.coords.longitude];
                this.zoom = 12;
            }

            this.open();
        },

        async submit(): Promise<void> {

            if (!this.valid) {
                return;
            }

            const station: Partial<Station> = {
                location: {
                    lat: this.result.latitude,
                    lon: this.result.longitude,
                },
                metadata: {
                    'elichens/public/entry_point': this.result.entryPoint,
                    'elichens/public/address': this.result.address,
                    'elichens/public/locality': this.result.city,
                    'elichens/public/postcode': this.result.postcode,
                    'elichens/public/state': this.result.state,
                    'elichens/public/country': this.result.country,
                    'elichens/public/location': this.result.address,
                    'elichens/public/installed': this.result.installed,
                }
            }
            this.failed = false;
            await this.stationStore.updateStation(this.stationToUpdate.id, station)
                .then(() => {
                    this.close(true);
                })
                .catch(() => {
                    this.failed = true;
                });

            this.$emit('submitted');
        },

        async search(query: string): Promise<void> {
            if (query && query.length > 0 && !this.loading) {

                this.loading = true;
                const response: GeocoderResponse = await this.$geocoder.search(query);

                this.autoCompleteOptions = response.results.map(result => {
                    return {
                        label: result.formatted_address,
                        value: result,
                    };
                });

                this.loading = false;

            }
        },

        onSearchSelect(result: GeocoderResult) {
            this.result.city = result.address_components.find(
                ad => [GeocoderAdressComponentTypeEnum.LOCALITY, GeocoderAdressComponentTypeEnum.ADMINISTRATIVE_LEVEL_3].some(type => ad.types.includes(type))
            )?.long_name;
            this.result.address = [
                result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.STREET_NUMBER))?.long_name,
                result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.ROUTE))?.long_name,
                result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.NEIGHBORHOOD))?.long_name,
            ].filter(str => str).join(', ');
            this.result.postcode = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.POSTAL_CODE))?.long_name;
            this.result.state = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.ADMINISTRATIVE_LEVEL_1))?.long_name;
            this.result.country = result.address_components.find(ad => ad.types.includes(GeocoderAdressComponentTypeEnum.COUNTRY))?.long_name;
           
            this.result.latitude = result.geometry.location.lat;
            this.result.longitude =  result.geometry.location.lng;

            this.mapStation = { ...this.mapStation, location: { lat: this.result.latitude, lon: this.result.longitude } }

        }

    },

    watch: {
        result: {
            async handler() {

                this.pristine = false;
                //@ts-ignore
                this.$refs?.formInst?.validate()
                    .then(() => this.valid = true)
                    .catch((err: Error) => {
                        if (err) {
                                this.valid = false;
                        }
                    });
                
                
               
            },
            deep: true,
        }
    }
});